import logger from "./logger";
import posthog from "posthog-js";
import { hasAnalyticsConsent } from "./consentManager";
import { SINGLE_EVENT_LAYOUT_VARIANTS } from "./singleEventLayout";
/**
 * PostHog Custom Event Tracking
 * Centralized funnel events for understanding user flows & drop-offs.
 *
 * Funnels built from these events:
 *
 * 🎫 Event Ticket Funnel:
 *   homepage_viewed → event_listing_viewed → event_detail_viewed → checkout_started → ticket_purchased
 *
 * 🎰 Spin & Win Funnel:
 *   spin_win_page_viewed → event_detail_viewed → checkout_started → spin_win_completed
 *
 * 💼 Lifestyle / Deals Funnel:
 *   lifestyle_page_viewed → deal_detail_viewed → deal_claimed
 *
 * 🏠 Housemate Funnel:
 *   housemate_list_viewed → housemate_detail_viewed → housemate_contact_clicked
 *
 * 🎮 Games Funnel:
 *   games_page_viewed → game_started → game_completed
 *
 * 👤 Account Funnel:
 *   login_page_viewed → account_creation_started → account_created
 */

// ─── Helper ──────────────────────────────────────────────────
// Uses the posthog-js singleton directly. The <PostHogProvider> in
// src/index.js initializes this same singleton via posthog.init(),
// so capture() always reaches the live instance (window.posthog is
// not reliably set by the React provider — the old gate silently
// dropped every custom event).
const capture = (eventName, properties = {}, sanitizeUrl = false) => {
  try {
    return posthog.capture(eventName, {
      platform: "web",
      ...properties,
      // Always include the current URL for context
      current_url: sanitizeUrl ? `${window.location.origin}${window.location.pathname}` : window.location.href,
      current_path: window.location.pathname,
      ...(sanitizeUrl ? { $current_url: `${window.location.origin}${window.location.pathname}` } : {}),
    });
  } catch (err) {
    // Silently fail — analytics should never break the app
    logger.error(`[PostHog] Error capturing ${eventName}:`, err);
  }
};

// Browser-level duplicate suppression only. The stable outcome ID also lets
// reports deduplicate across browsers; this is not a server delivery guarantee.
const capturedOutcomes = new Set();
const removeRejectedOutcomePersistence = (includePreviousSessions = false) => {
  if (hasAnalyticsConsent()) return;
  // An explicit revocation also removes markers written in earlier sessions.
  // Inspect only key names for cleanup; never read their stored values.
  if (includePreviousSessions) {
    try {
      for (let index = localStorage.length - 1; index >= 0; index -= 1) {
        const key = localStorage.key(index);
        if (key?.startsWith("22n_outcome_v2:")) localStorage.removeItem(key);
      }
    } catch { /* blocked storage */ }
  }
  capturedOutcomes.forEach((key) => {
    try { localStorage.removeItem(key); } catch { /* blocked storage */ }
  });
};
if (typeof window !== "undefined") window.addEventListener("22n:consent-change", () => removeRejectedOutcomePersistence(true));
const captureOutcome = (eventName, outcomeId, properties) => {
  const key = `22n_outcome_v2:${eventName}:${outcomeId}`;
  const persistent = hasAnalyticsConsent();
  if (!persistent) removeRejectedOutcomePersistence();
  if (capturedOutcomes.has(key)) return false;
  if (persistent) {
    try {
      if (localStorage.getItem(key)) { capturedOutcomes.add(key); return false; }
    } catch { /* memory fallback */ }
  }
  try { if (posthog.has_opted_out_capturing?.()) return false; } catch { return false; }
  // The SDK returns undefined when uninitialized, opted out, or before_send
  // drops the event. Do not mark those attempts as successfully captured.
  const accepted = capture(eventName, {
    ...properties, outcome_id: outcomeId, measurement_version: 2,
  }, true);
  if (!accepted) return false;
  capturedOutcomes.add(key);
  if (persistent) {
    try { localStorage.setItem(key, "1"); } catch { /* memory fallback */ }
  }
  return true;
};

const hasIdentity = (value) => (typeof value === "string" && value.trim().length > 0)
  || (typeof value === "number" && Number.isSafeInteger(value) && value > 0);

// ─── Homepage ────────────────────────────────────────────────
export const trackHomepageViewed = (properties = {}) => {
  capture('homepage_viewed', properties);
};

// ─── Event Listing (Browse Events) ──────────────────────────
export const trackEventListingViewed = ({ city, filters } = {}) => {
  capture('event_listing_viewed', { city, filters });
};

// ─── Event Detail ────────────────────────────────────────────
export const trackEventDetailViewed = ({ eventId, eventName, eventCategory, city, isPaid, date } = {}) => {
  capture('event_detail_viewed', {
    event_id: eventId,
    event_name: eventName,
    event_category: eventCategory,
    city,
    is_paid: isPaid,
    event_date: date,
  });
};

// ─── Single event layout experiment ─────────────────────────
// One exposure and one primary CTA intent per variant per JS page session,
// across event routes. Reloading starts a new session. This is a raw count
// ratio in Experiments, not unique-person attribution or a booking outcome.
const singleEventExposures = new Set();
const singleEventIntents = new Set();
const isEventExperimentPreview = (preview) => {
  if (preview) return true;
  try {
    return new URLSearchParams(window.location.search).get("preview") === "true"
      || /^\/admin(?:\/|$)/.test(window.location.pathname);
  } catch { return true; }
};

const captureSingleEventExperiment = (eventName, seen, {
  variant, eventId, source, preview = false, eventCategory, city,
} = {}) => {
  if (isEventExperimentPreview(preview)
    || !SINGLE_EVENT_LAYOUT_VARIANTS.includes(variant)
    || !hasIdentity(eventId)
    || seen.has(variant)) return false;
  try { if (posthog.has_opted_out_capturing?.()) return false; } catch { return false; }
  const accepted = capture(eventName, {
    layout_variant: variant,
    event_id: eventId,
    ...(source ? { button_location: source } : {}),
    ...(eventCategory ? { event_category: eventCategory } : {}),
    ...(city ? { city } : {}),
  }, true);
  // Do not consume the guard if the SDK rejects or drops the event.
  if (!accepted) return false;
  seen.add(variant);
  return true;
};

export const trackSingleEventExposed = (properties = {}) => captureSingleEventExperiment(
  "single_event_exposed", singleEventExposures, properties,
);

export const trackSingleEventCtaClicked = (properties = {}) => {
  // Only attribute an action to a layout actually exposed in this session.
  if (!singleEventExposures.has(properties.variant)) return false;
  return captureSingleEventExperiment("single_event_cta_clicked", singleEventIntents, properties);
};

// ─── Checkout Started ────────────────────────────────────────
export const trackCheckoutStarted = ({ eventId, eventName, eventCategory, city, isPaid, ticketPrice, currency } = {}) => {
  capture('checkout_started', {
    event_id: eventId,
    event_name: eventName,
    event_category: eventCategory,
    city,
    is_paid: isPaid,
    ticket_price: ticketPrice,
    currency,
  });
};

// ─── Free Guestlist Signup (inline on event page, no checkout) ──
export const trackGuestlistSignup = ({ eventId, eventName, eventCategory, city } = {}) => {
  capture('guestlist_signup', {
    event_id: eventId,
    event_name: eventName,
    event_category: eventCategory,
    city,
  });
};

// ─── Ticket Purchased / Conversion (Thanks Page) ────────────
// NOTE: was `ticket_claimed` — renamed to `ticket_purchased` to match the app
// (see docs/analytics-events.md §0). Cross-platform funnels depend on this name.
export const trackTicketPurchased = ({ orderId, eventId, eventName, eventCategory, city, totalCents, currency, paymentStatus, paymentIntentId, reservationId, source } = {}) => {
  if (!hasIdentity(orderId) || !hasIdentity(eventId) || paymentStatus !== "paid"
    || !Number.isSafeInteger(totalCents) || totalCents <= 0
    || !/^[a-z]{3}$/i.test(currency || "") || typeof paymentIntentId !== "string"
    || !paymentIntentId.trim() || paymentIntentId.trim().startsWith("sim_pi_")) return false;
  return captureOutcome('ticket_purchased', `order:${orderId}`, {
    order_id: orderId,
    reservation_id: reservationId,
    event_id: eventId,
    event_name: eventName,
    event_category: eventCategory,
    city,
    is_paid: true,
    amount_cents: totalCents,
    currency: currency.toLowerCase(),
    source: source || 'event', // 'event' | 'spin_win' | 'deal'
  });
};

/** Only an authoritative paid order read can classify checkout's outcome. */
export const trackConfirmedOrderOutcome = (order, { reservationId, event } = {}) => {
  if (!order || !hasIdentity(order.id) || !hasIdentity(order.event_id)
    || !hasIdentity(reservationId) || String(order.reservation_id) !== String(reservationId)
    || order.status !== "paid" || order.total === null || order.total === undefined
    || !["number", "string"].includes(typeof order.total) || String(order.total).trim() === ""
    || String(order.payment_intent_id || "").startsWith("sim_pi_")) return false;
  const totalCents = Number(order.total);
  if (!Number.isSafeInteger(totalCents) || totalCents < 0) return false;
  const matchingEvent = String(event?.id) === String(order.event_id) ? event : null;
  const properties = {
    orderId: order.id, eventId: order.event_id, reservationId,
    eventName: matchingEvent?.name || matchingEvent?.title,
    eventCategory: matchingEvent?.category, city: matchingEvent?.city,
    totalCents, currency: order.currency, paymentStatus: order.status,
    paymentIntentId: order.payment_intent_id, source: "event",
  };
  if (totalCents > 0) return trackTicketPurchased(properties);
  return captureOutcome("free_registration_completed", `order:${order.id}`, {
    order_id: order.id, reservation_id: reservationId, event_id: order.event_id,
    event_name: properties.eventName, event_category: properties.eventCategory,
    city: properties.city, is_paid: false, amount_cents: 0, source: "event",
  });
};

/** @deprecated Use trackTicketPurchased — kept for backwards compatibility. */
export const trackTicketClaimed = trackTicketPurchased;

// ─── Deal / Lifestyle Funnel ─────────────────────────────────
export const trackLifestylePageViewed = ({ city } = {}) => {
  capture('lifestyle_page_viewed', { city });
};

export const trackDealDetailViewed = ({ dealId, dealName, category, city } = {}) => {
  capture('deal_detail_viewed', {
    deal_id: dealId,
    deal_name: dealName,
    category,
    city,
  });
};

export const trackDealClaimed = ({ claimId, dealId, dealName, category, city, hasCode, isRsvp } = {}) => {
  if (!hasIdentity(claimId) || !hasIdentity(dealId)) return false;
  return captureOutcome('deal_claimed', `deal_claim:${claimId}`, {
    claim_id: claimId,
    deal_id: dealId,
    deal_name: dealName,
    category,
    city,
    has_code: hasCode,
    is_rsvp: !!isRsvp,
  });
};

export const trackConfirmedDealClaim = (response, deal) => {
  if (response?.success !== true || response.error
    || !hasIdentity(response.claim_id) || !hasIdentity(response.deal_id)
    || String(response.deal_id) !== String(deal?.id)) return false;
  return trackDealClaimed({
    claimId: response.claim_id, dealId: response.deal_id, dealName: deal.name,
    category: deal.category, city: deal.city, hasCode: !!response.code,
    isRsvp: response.redeem_category === "rsvp",
  });
};

// ─── Spin & Win ──────────────────────────────────────────────
export const trackSpinWinPageViewed = ({ city } = {}) => {
  capture('spin_win_page_viewed', { city });
};

export const trackSpinWinCompleted = ({ hasCode } = {}) => {
  capture('spin_win_completed', { has_code: hasCode });
};

export const trackSpinWinGuestSpin = ({ eventId } = {}) => {
  capture('spin_win_guest_spin', { event_id: eventId });
};

export const trackSpinWinGuestSignup = ({ eventId } = {}) => {
  capture('spin_win_guest_signup', { event_id: eventId });
};

// ─── Games ───────────────────────────────────────────────────
export const trackGamesPageViewed = () => {
  capture('games_page_viewed');
};

export const trackGameStarted = ({ gameMode, playerCount } = {}) => {
  capture('game_started', { game_mode: gameMode, player_count: playerCount });
};

export const trackGameCompleted = ({ gameMode, playerCount, roundsPlayed } = {}) => {
  capture('game_completed', { game_mode: gameMode, player_count: playerCount, rounds_played: roundsPlayed });
};

// ─── Housemates ──────────────────────────────────────────────
export const trackHousemateListViewed = ({ city, filters } = {}) => {
  capture('housemate_list_viewed', { city, filters });
};

export const trackHousemateDetailViewed = ({ listingId, city, listingType, price } = {}) => {
  capture('housemate_detail_viewed', {
    listing_id: listingId,
    city,
    listing_type: listingType,
    price,
  });
};

export const trackHousemateContactClicked = ({ listingId, city, method } = {}) => {
  capture('housemate_contact_clicked', {
    listing_id: listingId,
    city,
    method, // 'apply' | 'whatsapp' | 'email'
  });
};

// ─── Auth / Account ──────────────────────────────────────────
export const trackLoginPageViewed = ({ method } = {}) => {
  capture('login_page_viewed', { method }); // 'token' | 'email'
};

export const trackAccountCreationStarted = () => {
  capture('account_creation_started');
};

// Fires once per account that is actually created. Kept on the same event name
// for web, app and every auth method so a funnel never has to guess which
// client produced the signup. `method` is lower-case: 'email' | 'phone' |
// 'google' | 'apple'.
export const trackAccountCreated = ({ method, surface = 'web' } = {}) => {
  capture('account_created', { method, surface });
};

// ─── Shared / CTA Actions ───────────────────────────────────
export const trackEventShareClicked = ({ eventId, eventName, method } = {}) => {
  capture('event_share_clicked', {
    event_id: eventId,
    event_name: eventName,
    share_method: method,
  });
};

export const trackCtaClicked = ({ ctaName, page, properties } = {}) => {
  capture('cta_clicked', {
    cta_name: ctaName,
    page,
    ...properties,
  });
};

// ─── Event Engagement (docs/analytics-events.md §2.1) ───────
// Fired when the user leaves an event page after >15s on it.
export const trackEventEngaged = ({ eventId, durationSeconds, ...rest } = {}) => {
  capture('event_detail_engaged', {
    event_id: eventId,
    duration_seconds: durationSeconds,
    ...rest,
  });
};

// Fired on the 2nd+ view of the same event_id by the same browser.
export const trackEventViewedRepeat = ({ eventId, viewNumber, ...rest } = {}) => {
  capture('event_viewed_repeat', {
    event_id: eventId,
    view_number: viewNumber,
    ...rest,
  });
};

// ─── App Download Clicks (§2.2) ──────────────────────────────
// Any tap on an App Store / Google Play link. `buttonLocation` is one of the
// §2.2 values (home_city_pulse, home_discovery_hub, search_autocomplete,
// event_card_meta, event_signup_section, mobile_app_notice, single_event, ...).
// `variant` is only set while a surface is under A/B test (currently the
// app_download_banner: purple | glass | dock) — see docs/analytics/APP_BANNER_AB_TEST.md.
export const trackDownloadAppClick = ({ store = 'apple', buttonLocation, eventId, variant } = {}) => {
  capture('download_app_clicked', {
    store, // 'apple' | 'google'
    button_location: buttonLocation,
    event_id: eventId,
    ...(variant ? { banner_variant: variant } : {}),
  });
};

// ─── App Download Banner (A/B/C impression) ──────────────────
// Fired once per session per variant so the click-through rate of each bar can
// be computed as download_app_clicked / app_banner_viewed. Session guard lives
// here (not sessionStorage-forever): a new session counts again, which matches
// how the banner is shown to the same visitor across sessions.
const bannerSeenThisSession = new Set();
export const trackAppBannerViewed = ({ variant } = {}) => {
  if (!variant) return;
  const key = `app_banner:${variant}`;
  if (bannerSeenThisSession.has(key)) return;
  bannerSeenThisSession.add(key);
  capture('app_banner_viewed', {
    banner_variant: variant,
    button_location: 'app_download_banner',
  });
};

// ─── Community Chats (§2.3) ──────────────────────────────────
export const trackCommunityChatViewed = ({ chatId, chatType } = {}) => {
  capture('community_chat_viewed', {
    chat_id: chatId,
    chat_type: chatType,
  });
};

export const trackCommunityChatJoined = ({ chatId, chatType, eventId } = {}) => {
  capture('community_chat_joined', {
    chat_id: chatId,
    chat_type: chatType,
    event_id: eventId,
  });
};

export const trackCommunityFirstMessage = ({ chatId } = {}) => {
  capture('community_first_message', {
    chat_id: chatId,
  });
};

// ─── WhatsApp Group Status (thanks / deal success pages) ─────
// Fired once per category row we ask the user about on the success pages.
export const trackCommunityChatStatusPromptShown = ({ chatId, chatType, source } = {}) => {
  capture('community_chat_status_prompt_shown', {
    chat_id: chatId,
    chat_type: chatType,
    source,
  });
};

// Fired when the user answers "Join group" / "Already in" / "Used to be in".
export const trackCommunityChatStatusAnswered = ({ chatId, chatType, status, source } = {}) => {
  capture('community_chat_status_answered', {
    chat_id: chatId,
    chat_type: chatType,
    status,
    source,
  });
};

// ─── Extra Micro Conversions (§2.6) ──────────────────────────
export const trackEventFavorited = ({ eventId, eventName } = {}) => {
  capture('event_favorited', {
    event_id: eventId,
    event_name: eventName,
  });
};

// ─── Traffic Source Attribution (§2.5) ───────────────────────
// Fired once per browser session from the app root (sessionStorage guard
// lives at the call site in App.js).
export const trackLandingAttributed = ({ utmSource, utmMedium, utmCampaign, referringDomain, landingPath } = {}) => {
  capture('landing_attributed', {
    utm_source: utmSource,
    utm_medium: utmMedium,
    utm_campaign: utmCampaign,
    referring_domain: referringDomain,
    landing_path: landingPath,
  });
};

// ─── Search (§2.6) ───────────────────────────────────────────
// Fired when a search returns 0 results, so content gaps are alertable.
export const trackSearchZeroResults = ({ query } = {}) => {
  capture('search_zero_results', { query });
};
